/**
 * DataTable Component - MUI-based table with multi-select and bulk actions
 * Supports search, pagination, and row selection
 */

import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Card,
  CardContent,
  CardHeader,
  TextField,
  InputAdornment,
  Button,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Checkbox,
  Box,
  Tooltip,
  IconButton,
  CircularProgress,
  Chip,
  Grid,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
} from "@mui/icons-material";

/**
 * Resolve column sizing. Prefer explicit maxWidth so cells never grow past a viewport share.
 * When maxWidth is set, overflow is clipped so cell renderers can ellipsis + tooltip.
 */
const getColumnSizeSx = (column, fitWidth) => {
  const width = column.width;
  const maxWidth = column.maxWidth ?? (fitWidth && width != null ? width : undefined);
  const minWidth =
    column.minWidth ?? (fitWidth ? 0 : width != null ? width : undefined);
  const shouldClip = fitWidth || maxWidth != null;

  return {
    ...(width != null ? { width } : null),
    ...(minWidth != null ? { minWidth } : null),
    ...(maxWidth != null ? { maxWidth } : null),
    overflow: shouldClip ? "hidden" : "visible",
  };
};

const DataTable = ({
  data = [],
  columns = [],
  loading = false,
  title = "Data Table",
  searchPlaceholder = "Search...",
  searchFields = [],
  onView,
  onEdit,
  onRefresh,
  pageSize = 10,
  showSearch = true,
  showPagination = true,
  showRefresh = true,
  extraActions,
  rowKey = "id",
  // Multi-select props
  enableRowSelection = false,
  selectedRowKeys = [],
  onSelectionChange,
  bulkActions = [],
  getCheckboxProps,
  // Custom row click handler
  onRowClick,
  tableContainerSx = null,
  tableMinWidth = 650,
  fitWidth = false,
}) => {
  // Valid page size options for Material-UI TablePagination
  const validPageSizes = [5, 10, 25, 50, 100];

  // Validate and normalize pageSize to ensure it's in the valid options
  const normalizedPageSize = useMemo(() => {
    if (validPageSizes.includes(pageSize)) {
      return pageSize;
    }
    // Find the closest valid option (prefer smaller if equidistant)
    const closest = validPageSizes.reduce((prev, curr) => {
      return Math.abs(curr - pageSize) < Math.abs(prev - pageSize)
        ? curr
        : prev;
    });
    return closest;
  }, [pageSize]);

  const [searchText, setSearchText] = useState("");
  const [searchField, setSearchField] = useState(searchFields[0] || "all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(normalizedPageSize);
  const [selected, setSelected] = useState(selectedRowKeys || []);

  // Sync selected state with prop if controlled
  useEffect(() => {
    if (selectedRowKeys !== undefined && selectedRowKeys !== null) {
      setSelected(selectedRowKeys);
    }
  }, [selectedRowKeys]);

  // Helper function to get row ID - handles both string and function rowKey
  const getRowId = (row) => {
    if (typeof rowKey === "function") {
      return rowKey(row);
    }
    return row[rowKey];
  };

  // Sync rowsPerPage when normalizedPageSize changes (when pageSize prop changes)
  useEffect(() => {
    setRowsPerPage(normalizedPageSize);
    setPage(0); // Reset to first page when page size changes
  }, [normalizedPageSize]);

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!searchText) return data;

    return data.filter((item) => {
      if (searchField === "all") {
        // Search across all searchable fields
        return searchFields.some((field) => {
          const value = item[field];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(searchText.toLowerCase());
        });
      } else {
        // Search in specific field
        const value = item[searchField];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(searchText.toLowerCase());
      }
    });
  }, [data, searchText, searchField, searchFields]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!showPagination) return filteredData;

    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, page, rowsPerPage, showPagination]);

  // Handle search
  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setPage(0); // Reset to first page on search
  };

  // Handle search field change
  const handleSearchFieldChange = (event) => {
    setSearchField(event.target.value);
    setSearchText(""); // Clear search when field changes
    setPage(0);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle refresh
  const handleRefresh = () => {
    setSearchText("");
    setPage(0);
    if (onRefresh) onRefresh();
  };

  // Selection handlers
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const selectableRows = paginatedData.filter((row) => {
        if (getCheckboxProps) {
          const props = getCheckboxProps(row);
          return !props?.disabled;
        }
        return true;
      });
      const newSelected = selectableRows.map((row) => getRowId(row));
      setSelected(newSelected);
      if (onSelectionChange) {
        onSelectionChange(newSelected);
      }
    } else {
      setSelected([]);
      if (onSelectionChange) {
        onSelectionChange([]);
      }
    }
  };

  const handleRowClick = (event, row) => {
    // Don't trigger row click if clicking on interactive elements
    const target = event.target;
    const isInteractiveElement =
      target.closest("button") ||
      target.closest("a") ||
      target.closest('[role="button"]') ||
      target.closest(".ant-dropdown") ||
      target.closest(".ant-dropdown-menu") ||
      target.closest(".ant-dropdown-trigger") ||
      target.closest('input[type="checkbox"]') || // Checkbox is handled separately
      target.closest("label"); // Label for checkbox

    if (isInteractiveElement) {
      return;
    }

    // If custom row click handler provided, use it
    if (onRowClick) {
      onRowClick(row);
      return;
    }

    // Otherwise, handle selection if enabled
    if (!enableRowSelection) return;

    // Check if row is disabled
    const checkboxProps = getCheckboxProps ? getCheckboxProps(row) : {};
    if (checkboxProps.disabled) {
      return;
    }

    const rowId = getRowId(row);
    const selectedIndex = selected.indexOf(rowId);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, rowId];
    } else {
      newSelected = selected.filter((id) => id !== rowId);
    }

    setSelected(newSelected);
    if (onSelectionChange) {
      onSelectionChange(newSelected);
    }
  };

  // Handler for individual checkbox changes
  const handleCheckboxChange = (event, row) => {
    event.stopPropagation();

    const checkboxProps = getCheckboxProps ? getCheckboxProps(row) : {};
    if (checkboxProps.disabled) {
      return;
    }

    const rowId = getRowId(row);
    const selectedIndex = selected.indexOf(rowId);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, rowId];
    } else {
      newSelected = selected.filter((id) => id !== rowId);
    }

    setSelected(newSelected);
    if (onSelectionChange) {
      onSelectionChange(newSelected);
    }
  };

  const isSelected = (rowId) => selected.indexOf(rowId) !== -1;

  const isIndeterminate = () => {
    const selectableRows = paginatedData.filter((row) => {
      if (getCheckboxProps) {
        const props = getCheckboxProps(row);
        return !props?.disabled;
      }
      return true;
    });
    return (
      selectableRows.length > 0 &&
      selected.length > 0 &&
      selected.length < selectableRows.length
    );
  };

  const isAllSelected = () => {
    const selectableRows = paginatedData.filter((row) => {
      if (getCheckboxProps) {
        const props = getCheckboxProps(row);
        return !props?.disabled;
      }
      return true;
    });
    return (
      selectableRows.length > 0 && selected.length === selectableRows.length
    );
  };

  // Get selected rows data
  const getSelectedRows = () => {
    return data.filter((row) => selected.includes(getRowId(row)));
  };

  // Render cell content based on column definition
  const renderCellContent = (column, record) => {
    if (column.render) {
      return column.render(record[column.dataIndex], record, getRowId(record));
    }
    if (column.dataIndex) {
      const value = record[column.dataIndex];
      return value !== null && value !== undefined ? String(value) : "N/A";
    }
    return null;
  };

  // Add action columns if callbacks provided
  const tableColumns = useMemo(() => {
    const baseColumns = [...columns];

    if (onView || onEdit) {
      baseColumns.push({
        title: "Actions",
        key: "actions",
        width: 120,
        align: "right",
        render: (_, record) => (
          <Box display="flex" gap={1} justifyContent="flex-end">
            {onView && (
              <Tooltip title="View Details">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onView(record);
                  }}
                  color="primary"
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(record);
                  }}
                  color="primary"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      });
    }

    return baseColumns;
  }, [columns, onView, onEdit, rowKey]);

  return (
    <Card
      sx={{
        mb: 2,
        width: "100%",
        maxWidth: "100%",
        // Horizontal scroll lives on TableContainer below
        overflow: "hidden",
      }}
    >
      <CardHeader
        title={title}
        action={
          <Box display="flex" gap={1} alignItems="center">
            {showRefresh && (
              <Button
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
                size="small"
              >
                Refresh
              </Button>
            )}
            {extraActions}
          </Box>
        }
      />
      <CardContent>
        {/* Search and Controls */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {showSearch && (
            <Grid item xs={12} sm={6} md={4}>
              <Box display="flex" gap={1}>
                {searchFields.length > 1 && (
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={searchField}
                      onChange={handleSearchFieldChange}
                      displayEmpty
                    >
                      <MenuItem value="all">All Fields</MenuItem>
                      {searchFields.map((field) => (
                        <MenuItem key={field} value={field}>
                          {field.charAt(0).toUpperCase() + field.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <TextField
                  fullWidth
                  size="small"
                  placeholder={searchPlaceholder}
                  value={searchText}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Grid>
          )}

          <Grid item xs={12} sm={showSearch ? 6 : 12} md={showSearch ? 8 : 12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {paginatedData.length} of {filteredData.length} items
                {searchText && ` (filtered by "${searchText}")`}
              </Typography>
              {enableRowSelection && selected.length > 0 && (
                <Box display="flex" gap={1} alignItems="center">
                  <Chip
                    label={`${selected.length} selected`}
                    color="primary"
                    size="small"
                  />
                  {bulkActions.map((action, index) => (
                    <Button
                      key={index}
                      variant={action.variant || "contained"}
                      color={action.color || "primary"}
                      size="small"
                      startIcon={action.icon}
                      onClick={() => {
                        if (action.onClick) {
                          action.onClick(getSelectedRows(), selected);
                        }
                      }}
                    >
                      {action.label}
                    </Button>
                  ))}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Table */}
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            overflowX: fitWidth ? "auto" : "auto",
            maxWidth: "100%",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(0,0,0,0.28) transparent",
            "&::-webkit-scrollbar": {
              height: 6,
              width: 6,
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.28)",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "rgba(0,0,0,0.4)",
            },
            ...(tableContainerSx || {}),
          }}
        >
          {loading && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={200}
            >
              <CircularProgress />
            </Box>
          )}
          {!loading && (
            <Table
              sx={{
                width: "100%",
                tableLayout: fitWidth ? "fixed" : "auto",
                ...(fitWidth
                  ? { minWidth: 0 }
                  : { minWidth: tableMinWidth || 650 }),
              }}
            >
              <TableHead>
                <TableRow>
                  {enableRowSelection && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={isIndeterminate()}
                        checked={isAllSelected()}
                        onChange={handleSelectAllClick}
                        inputProps={{
                          "aria-label": "select all rows",
                        }}
                      />
                    </TableCell>
                  )}
                  {tableColumns.map((column) => (
                    <TableCell
                      key={column.key || column.dataIndex}
                      align={column.align || "left"}
                      sx={{
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        ...getColumnSizeSx(column, fitWidth),
                      }}
                    >
                      {column.title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={
                        tableColumns.length + (enableRowSelection ? 1 : 0)
                      }
                      align="center"
                      sx={{ py: 4 }}
                    >
                      <Typography color="text.secondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((record) => {
                    const rowId = getRowId(record);
                    const isRowSelected = isSelected(rowId);
                    const checkboxProps = getCheckboxProps
                      ? getCheckboxProps(record)
                      : {};

                    return (
                      <TableRow
                        key={rowId}
                        hover
                        selected={isRowSelected}
                        onClick={(e) => handleRowClick(e, record)}
                        sx={{
                          cursor:
                            onRowClick || enableRowSelection
                              ? "pointer"
                              : "default",
                          "&:hover": {
                            backgroundColor:
                              onRowClick || enableRowSelection
                                ? "action.hover"
                                : "transparent",
                          },
                        }}
                      >
                        {enableRowSelection && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isRowSelected}
                              disabled={checkboxProps.disabled}
                              onChange={(e) => handleCheckboxChange(e, record)}
                              inputProps={{
                                "aria-labelledby": `row-${rowId}`,
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                        )}
                        {tableColumns.map((column) => (
                          <TableCell
                            key={column.key || column.dataIndex}
                            align={column.align || "left"}
                            sx={{
                              whiteSpace: "nowrap",
                              verticalAlign: "top",
                              ...getColumnSizeSx(column, fitWidth),
                            }}
                          >
                            {renderCellContent(column, record)}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Pagination */}
        {showPagination && filteredData.length > rowsPerPage && (
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={validPageSizes}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count}`
            }
          />
        )}
      </CardContent>
    </Card>
  );
};

export default DataTable;
